"use client";

import React from "react";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import ItemCard from "@/components/ui/ItemCard";
import MobileActionPanel from "@/components/ui/MobileActionPanel";
import PriceFilter from "@/components/ui/PriceFilter";
import LocationLoginModal from "@/components/ui/LocationLoginModal";
import RecentlyConfirmedShelf from "@/components/ui/RecentlyConfirmedShelf";
import ItemPreviewModal from "@/components/ui/ItemPreviewModal";
import MarketNotificationBannerSlot from "@/components/market/MarketNotificationBannerSlot";
import MarketPageHeader from "@/components/market/MarketPageHeader";
import MarketFilterControls from "@/components/market/MarketFilterControls";
import MarketResultsBar from "@/components/market/MarketResultsBar";
import MarketActiveFilters from "@/components/market/MarketActiveFilters";
import MarketCategorySelector from "@/components/market/MarketCategorySelector";
import MarketEmptyState from "@/components/market/MarketEmptyState";
import MarketItemsGrid from "@/components/market/MarketItemsGrid";
import { useMarketBrowse } from "@/lib/hooks/useMarketBrowse";
import {
  buildMarketResultsSummary,
  buildMarketCatalogCapNotice,
} from "@/lib/market/resultsSummary";
import { MARKET_COPY } from "@/lib/market/copy";
import type { ProcessedItem } from "@/lib/market/types";
import {
  captureMarketItemClick,
  type MarketItemClickSource,
} from "@/lib/analytics/captureMarketItemClick";
import type { Item } from "@/lib/types";

export default function MarketView() {
  const {
    settingsLoading,
    nearMePlatformEnabled,
    selectedCategory,
    selectedLocation,
    searchQuery,
    sortBy,
    priceRange,
    viewMode,
    locationOptions,
    activeCategoryStructure,
    defaultSearchValue,
    itemsData,
    displayedTotal,
    error,
    isValidating,
    filteredItems,
    relatedItems,
    isCalculatingDistances,
    nearYouLocationContext,
    catalogCapped,
    activeFilterChips,
    emptyStateVariant,
    showRelatedInEmptyState,
    categoryQuickPicks,
    subcategoryPicks,
    showRecentlyConfirmedShelf,
    locationEndBannerInsertIndex,
    hasMore,
    loadMore,
    isMounted,
    isLoadingLocation,
    itemShare,
    itemPreview,
    showLoginModal,
    setShowLoginModal,
    onUseDefaultSort,
    handleLocationSelect,
    handleCategorySelect,
    handleSortChange,
    handleViewModeChange,
    handlePriceRangeChange,
    handleSearch,
    handleRemoveFilter,
    handleClearAllFilters,
    navigateToRequestItem,
  } = useMarketBrowse();

  const hasActiveFilters =
    activeFilterChips.length > 0 || Boolean(searchQuery.trim());

  /** Single market click owner: capture browse→open, then open preview. */
  const openMarketItem = (item: Item, source: MarketItemClickSource): void => {
    captureMarketItemClick({
      listingId: item.id,
      category: item.category,
      subcategory: item.subcategory,
      listingPrice: item.price,
      source,
      sort: sortBy,
      hasFilters: hasActiveFilters,
    });
    itemPreview.openPreview(item);
  };

  // Emptiness derives from the displayed (eligible) count — the single source
  // of truth — so it never contradicts the results bar (R1/R2). It's also
  // distance-independent, so the empty state doesn't flash while sorting.
  const isEmptyResults = displayedTotal === 0 && !isValidating;
  const emptyVariant = error
    ? "error"
    : emptyStateVariant === "search"
      ? "search"
      : emptyStateVariant === "filtered"
        ? "filtered"
        : "plain";

  const renderRelatedShelf = (heading: string) => (
    <section aria-label={heading}>
      <h2 className="text-lg font-semibold text-foreground mb-4">{heading}</h2>
      <div
        className={
          viewMode === "grid"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-4"
        }
      >
        {relatedItems.map((item) => {
          const processedItem = item as ProcessedItem;
          return (
            <ItemCard
              key={item.id}
              item={item}
              viewMode={viewMode}
              onClick={() => openMarketItem(item, "related")}
              distance={processedItem.distance}
              disableDistanceAutoCalculation={true}
              itemShareUrl={
                itemShare.showItemShare
                  ? itemShare.buildItemShareUrl(item.id) || undefined
                  : undefined
              }
              partnerShareToolsEnabled={
                itemShare.showItemShare && itemShare.partnerShareToolsEnabled
              }
            />
          );
        })}
      </div>
    </section>
  );

  // Location no longer gates the whole page (Phase 2): the listing shell renders
  // immediately and location-dependent sorts show a non-blocking inline status.
  const showLocationSortStatus =
    isLoadingLocation && (sortBy === "proximity" || sortBy === "newest");

  const resultsSummary = itemsData
    ? buildMarketResultsSummary({
        total: displayedTotal,
        searchQuery,
        selectedCategory: selectedCategory?.categoryId || "all",
        selectedLocation,
        nearYouArea: nearYouLocationContext?.areaName ?? "",
      })
    : "";

  const capNotice =
    itemsData && catalogCapped
      ? buildMarketCatalogCapNotice(displayedTotal)
      : null;

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MarketPageHeader locationContext={nearYouLocationContext} />
          {!settingsLoading ? (
            <MarketNotificationBannerSlot
              nearMePlatformEnabled={nearMePlatformEnabled}
            />
          ) : null}
          {/* Filters — inline location from lg; below that, location lives in MobileActionPanel */}
          <div className="sio-glass rounded-sio-lg p-4 mb-6">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
              <MarketFilterControls
                locationOptions={locationOptions}
                selectedLocation={selectedLocation}
                onLocationSelect={handleLocationSelect}
                onSearch={handleSearch}
                defaultSearchValue={defaultSearchValue}
              />
            </div>
          </div>

          {/* Active filter chips (Phase 4, D4) */}
          <MarketActiveFilters
            chips={activeFilterChips}
            onRemove={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
          />

          {/* Desktop chrome (sidebar) from lg — medium keeps full-width feed + Filter panel */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Desktop Filter Sidebar (category selection now lives in the
                single MarketCategorySelector chip row in the content column) */}
            <div className="hidden lg:block w-full lg:w-64 flex-shrink-0">
              <div className="sio-surface-card p-4 space-y-4">
                <PriceFilter
                  priceRange={priceRange}
                  onPriceRangeChange={handlePriceRangeChange}
                />
              </div>
            </div>

            {/* Main Content Area — min-w-0 so flex child can shrink inside max-w-7xl (no page x-scroll) */}
            <div className="min-w-0 flex-1">
              {/* Non-blocking status while location resolves for a location-based sort */}
              {showLocationSortStatus && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-2 py-2 px-3 mb-4 rounded-sio bg-primary/10 text-primary text-sm"
                >
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                  <span>{MARKET_COPY.sortRefiningLocation}</span>
                </div>
              )}

              {/* Non-blocking indicator when sorting by distance */}
              {isCalculatingDistances && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-2 py-2 px-3 mb-4 rounded-sio bg-primary/10 text-primary text-sm"
                >
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                  <span>Sorting by distance...</span>
                </div>
              )}

              {/* Single category surface: nested category + refine chips
                  (Phase 6.2). Category rail stays visible when refining. */}
              <MarketCategorySelector
                quickPicks={categoryQuickPicks}
                subcategoryPicks={subcategoryPicks}
                selectedCategory={selectedCategory}
                categoryStructure={activeCategoryStructure}
                onCategorySelect={handleCategorySelect}
              />

              {showRecentlyConfirmedShelf ? (
                <div className="mb-8">
                  <RecentlyConfirmedShelf
                    onItemClick={(item) =>
                      openMarketItem(item, "confirmed_shelf")
                    }
                  />
                </div>
              ) : null}

              {/* Search Results Summary with Sort and View Options.
                  Hidden when nothing renders so the empty state owns the
                  "no results" message (avoids a duplicate/contradictory count). */}
              {!error &&
                !isValidating &&
                itemsData?.items &&
                !isEmptyResults && (
                  <MarketResultsBar
                    summary={resultsSummary}
                    capNotice={capNotice}
                    sortBy={sortBy}
                    viewMode={viewMode}
                    onSortChange={handleSortChange}
                    onViewModeChange={handleViewModeChange}
                  />
                )}

              {/* Loading state */}
              {isValidating && !itemsData?.items.length && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-tertiary rounded-lg h-48 mb-4" />
                      <div className="h-4 bg-tertiary rounded w-3/4 mb-2" />
                      <div className="h-4 bg-tertiary rounded w-1/2" />
                    </div>
                  ))}
                </div>
              )}

              {/* Error state */}
              {error && (
                <MarketEmptyState
                  variant="error"
                  onRetry={() => window.location.reload()}
                  onClearFilters={handleClearAllFilters}
                  onRequestItem={navigateToRequestItem}
                />
              )}

              {/* Items grid/list with conditional rendering to avoid hydration mismatch */}
              {!error && (
                <>
                  <MarketItemsGrid
                    filteredItems={filteredItems}
                    viewMode={viewMode}
                    locationEndBannerInsertIndex={locationEndBannerInsertIndex}
                    itemShare={itemShare}
                    isMounted={isMounted}
                    onItemClick={(item) =>
                      openMarketItem(
                        item,
                        viewMode === "list" ? "list" : "grid",
                      )
                    }
                  />

                  {/* Explicit "Load more" pagination (R3) — replaces infinite
                      scroll so loading is buyer-triggered and predictable. */}
                  {hasMore && !isEmptyResults && (
                    <div className="flex justify-center items-center py-8">
                      <button
                        type="button"
                        onClick={loadMore}
                        disabled={isValidating}
                        className="inline-flex items-center gap-2 rounded-sio border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-tertiary disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isValidating ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                            {MARKET_COPY.loadMoreLoadingLabel}
                          </>
                        ) : (
                          MARKET_COPY.loadMoreCta
                        )}
                      </button>
                    </div>
                  )}

                  {/* Supplementary related shelf below actual results (search) */}
                  {!isEmptyResults &&
                    searchQuery.trim() &&
                    relatedItems.length > 0 && (
                      <div className="mt-10 pt-8 border-t border-border">
                        {renderRelatedShelf(
                          MARKET_COPY.relatedSuggestionsHeading,
                        )}
                      </div>
                    )}

                  {/* Empty state: headline → related shelf → recovery CTA (C6/C7) */}
                  {isEmptyResults && (
                    <MarketEmptyState
                      variant={emptyVariant}
                      searchQuery={searchQuery}
                      hasRelated={showRelatedInEmptyState}
                      relatedSlot={
                        showRelatedInEmptyState
                          ? renderRelatedShelf(MARKET_COPY.relatedHeading)
                          : undefined
                      }
                      onRetry={() => window.location.reload()}
                      onClearFilters={handleClearAllFilters}
                      onRequestItem={navigateToRequestItem}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <LocationLoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onUseDefault={onUseDefaultSort}
            description="To sort items by proximity to your location, you need to be logged in."
            helperText={MARKET_COPY.guestNearestHelper}
          />

          {/* Quick-view preview: Buy → /item?buy=1; Find more closes on market. */}
          <ItemPreviewModal
            itemId={itemPreview.selectedItemId}
            isOpen={itemPreview.selectedItemId !== null}
            onClose={itemPreview.closePreview}
            initialItem={itemPreview.selectedItem || undefined}
            surface="market"
          />

          {/* Mobile Action Panel */}
          <MobileActionPanel
            sortBy={sortBy}
            viewMode={viewMode}
            priceRange={priceRange}
            selectedLocation={selectedLocation}
            locationOptions={locationOptions}
            onSortChange={handleSortChange}
            onViewModeChange={handleViewModeChange}
            onPriceRangeChange={handlePriceRangeChange}
            onLocationSelect={handleLocationSelect}
            hasActiveFilters={Boolean(
              selectedLocation || priceRange.min || priceRange.max,
            )}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}
